import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

interface SvgContent {
  isSvg: boolean;
  shouldInlineSvg: boolean;
  svgContent: string;
  svgError: boolean;
}

/**
 * Fetches an SVG at `path`, sanitizes it with DOMPurify, and returns markup suitable for inlining.
 * Inlining (rather than an <img>) is required so branding classes inside the SVG can be targeted by the host app's styles.
 * Non-SVG paths are a no-op: `isSvg` is false and `svgContent` stays empty.
 */
export const useSvgContent = (path: string): SvgContent => {
  const [svgContent, setSvgContent] = useState('');
  const [svgError, setSvgError] = useState(false);

  const isSvg = useMemo(() => /\.svg([?#]|$)/i.test(path), [path]);
  const shouldInlineSvg = useMemo(() => {
    // These paths should render natively as <img> to avoid sanitizer side-effects.
    const isNativeSvgPath = /^(?:data|blob):|^\/src\/images\/|^\/assets\//i.test(path);

    return isSvg && !isNativeSvgPath;
  }, [isSvg, path]);

  useEffect(() => {
    if (!shouldInlineSvg) {
      setSvgContent('');
      setSvgError(false);
      return;
    }

    let mounted = true;
    const controller = new AbortController();
    setSvgContent('');
    setSvgError(false);

    fetch(path, { signal: controller.signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG (${response.status})`);
        }
        return response.text();
      })
      .then(svg => {
        if (!mounted) return;

        const sanitizedSvg = DOMPurify.sanitize(svg, {
          USE_PROFILES: {
            svg: true,
            svgFilters: true,
          },
        });

        if (!sanitizedSvg.trim()) {
          setSvgError(true);
          return;
        }

        setSvgContent(sanitizedSvg);
      })
      .catch((error: { name?: string }) => {
        if (error?.name === 'AbortError') {
          return;
        }

        if (mounted) {
          setSvgError(true);
        }
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [shouldInlineSvg, path]);

  return { isSvg, shouldInlineSvg, svgContent, svgError };
};
